import React from 'react';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export interface ChipOption {
  value: string;
  label: string;
}

interface SettingChipProps {
  /** Setting name — used for the tooltip, aria label and the empty-value text. */
  title: string;
  /** 14px leading icon, colored by `iconColor`. */
  icon?: React.ReactNode;
  iconColor?: string;
  /** What the chip shows (the option's label); empty falls back to `title` muted. */
  display: string;
  value: string;
  options: ChipOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Toolbar variant: tighter metrics, no long-label guarantees. */
  compact?: boolean;
}

/**
 * A pill dropdown in the customer-messenger's refinement-strip style: icon +
 * value + chevron, with a native `<select>` laid invisibly over the whole pill
 * so it behaves (and stays accessible) without a popup library. The value is
 * always shown — every AI setting has one — and the tooltip reads "Tone:
 * Friendly", exactly like the CRM's chips.
 */
export const SettingChip: React.FC<SettingChipProps> = ({
  title,
  icon,
  iconColor,
  display,
  value,
  options,
  onChange,
  disabled,
  compact,
}) => {
  // A value outside the option list (e.g. a persona deleted from the CRM)
  // must still show — prepend it so the select doesn't fall back visually.
  const normalized =
    value && !options.some((option) => option.value === value)
      ? [{ value, label: display || value }, ...options]
      : options;

  return (
    <ChipWrapper
      title={`${title}: ${display || value || '—'}`}
      $compact={Boolean(compact)}
      $disabled={Boolean(disabled)}
      $hasValue={Boolean(display || value)}
    >
      {icon && <ChipIcon $color={iconColor}>{icon}</ChipIcon>}
      <ChipText $muted={!display && !value}>{display || title}</ChipText>
      <ChipChevron>
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      </ChipChevron>
      <select
        value={value}
        disabled={disabled}
        aria-label={title}
        onChange={(event) => onChange(event.target.value)}
      >
        {normalized.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </ChipWrapper>
  );
};

const ChipWrapper = styled.div<{
  $compact?: boolean;
  $disabled?: boolean;
  $hasValue?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${({ $compact }) => ($compact ? '4px 8px' : '4px 6px')};
  min-height: ${({ $compact }) => ($compact ? '28px' : '32px')};
  box-sizing: border-box;
  border: 1px solid ${({ $hasValue }) => ($hasValue ? '#afa9ee' : '#d0d5dd')};
  border-radius: 16px;
  background: #ffffff;
  font-size: ${({ $compact }) => ($compact ? '12.5px' : '13px')};
  font-family: inherit;
  white-space: nowrap;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $disabled }) => ($disabled ? '#ffffff' : '#f0effc')};
    border-color: ${({ $disabled }) => ($disabled ? '#d0d5dd' : '#afa9ee')};
  }

  &:focus-within {
    outline: 2px solid #c8c4f3;
    outline-offset: 2px;
  }

  /* The real control: invisible but covering the whole pill. */
  select {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    border: none;
    margin: 0;
    cursor: inherit;

    &:disabled {
      cursor: default;
    }
  }
`;

const ChipIcon = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $color }) => $color || '#667085'};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ChipText = styled.span<{ $muted?: boolean }>`
  font-weight: ${({ $muted }) => ($muted ? 600 : 500)};
  color: ${({ $muted }) => ($muted ? '#98a2b3' : '#4c34c2')};
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 170px;
`;

const ChipChevron = styled.span`
  display: inline-flex;
  align-items: center;
  color: #667085;
  flex-shrink: 0;
`;

export default SettingChip;
